import React from 'react';
import { Box, TextField, Typography } from '@mui/material';

export interface RangeInputProps {
  value: string;
  onRangeChange: (min: number | string, max: number | string) => void;
  extractRangeValues: (value: string) => { min: number | string, max: number | string };
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  label?: string;
}

/**
 * Generic component for rendering range inputs (min-max)
 * Can be used for price points, quantities, section numbers, etc.
 */
const RangeInput: React.FC<RangeInputProps> = ({ 
  value, 
  onRangeChange, 
  extractRangeValues, 
  onBlur, 
  error, 
  helperText,
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  label
}) => {
  // Ensure value is a string to avoid errors
  const safeValue = typeof value === 'string' ? value : '0,0';
  // Extract min and max values from the string (e.g. "10,20")
  const { min, max } = extractRangeValues(safeValue);
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {label && (
        <Typography sx={{ fontWeight: '600', fontSize: '14px', mb: 1 }}>{label}</Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: label ? 0 : 1 }}>
        <TextField
          size="small"
          placeholder={minPlaceholder}
          value={min}
          onChange={(e) => {
            onRangeChange(e.target.value, max);
          }}
          onBlur={onBlur}
          sx={{ width: '45%' }}
          error={error}
        />
        <Typography sx={{ lineHeight: '32px' }}>-</Typography>
        <TextField
          size="small"
          placeholder={maxPlaceholder}
          value={max}
          onChange={(e) => {
            onRangeChange(min, e.target.value);
          }}
          onBlur={onBlur}
          sx={{ width: '45%' }}
          error={error}
        />
      </Box>
      {helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RangeInput;
